import { NavigateFunction } from "react-router-dom";

import {
  ApiService,
  Case,
  CaseDiagnosisAddonWritable,
  CaseDiagnosis,
  CaseReport,
  User,
} from "pwapi";

// import { CaseDiagnosis } from "custom-models";

import {
  CASE_DETAIL_REQUEST,
  WIPE_CASE_DETAIL,
  CASE_DETAIL_EVENTS_REQUEST,
  CASE_PATCH_REQUEST,
  CASE_REQUEST_SCREEN_REQUEST,
  CASE_OPEN_DOCUMENT_REQUEST,
  CASE_DIAGNOSES_REQUEST,
  DIAGNOSE_REQUEST,
  CASE_SIGN_OUT_REQUEST,
  UPDATE_DIAGNOSIS_REQUEST,
  STAIN_ORDER_REQUEST,
  CASE_PATCH_STATUS_REQUEST,
  UPDATE_CASE_ADDON_REQUEST,
  CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST,
  EDIT_SCREEN_DIAGNOSES_REQUEST,
  CASE_UNDO_SIGN_OUT_REQUEST,
  CASE_PATIENT_MATCHES_REQUEST,
  CASE_PATIENT_MATCHES_SUCCESS,
  CASE_PATIENT_MATCHES_FAILURE,
  MODIFY_REPORT_REQUEST,
  CANCEL_MODIFY_REPORT_REQUEST,
  REPORT_SIGNEE_FAILURE,
  REPORT_SIGNEE_REQUEST,
  REPORT_SIGNEE_SUCCESS,
  CASE_UPDATE_SPECIMEN_REQUEST,
  CASE_UPDATE_SPECIMEN_SUCCESS,
  CASE_UPDATE_SPECIMEN_FAILURE,
  STAIN_ORDER_SUCCESS,
  STAIN_ORDER_FAILURE,
} from "./constants";
import {
  AsyncSagaAction,
  AsyncSagaActionNoArgs,
  createAsyncSaga,
} from "../utils/asyncActions";

export const caseDetail = (id: number) => ({
  type: CASE_DETAIL_REQUEST,
  payload: { id },
});

export const wipeCaseDetail = () => ({
  type: WIPE_CASE_DETAIL,
});

export const caseLog = (caseId: number) => ({
  type: CASE_DETAIL_EVENTS_REQUEST,
  payload: { parentLookupCaseId: caseId },
});

export const patchCaseWorkflow = (payload: any) => ({
  type: CASE_PATCH_REQUEST,
  payload,
});

export const patchCaseStatus = (payload: any) => ({
  type: CASE_PATCH_STATUS_REQUEST,
  payload,
});

export const patchCaseAddon = (
  id: number,
  type: CaseDiagnosisAddonWritable.addon_type,
  content: string
) => ({
  type: UPDATE_CASE_ADDON_REQUEST,
  payload: { id, type, content },
});

export const requestScreen = (id?: number) => ({
  type: CASE_REQUEST_SCREEN_REQUEST,
  payload: {
    id,
  },
});

export const openDocument = (
  caseId: number,
  documentId: number,
  source?: string,
  user?: User,
  downloadFileName?: string
) => ({
  type: CASE_OPEN_DOCUMENT_REQUEST,
  payload: {
    parentLookupCaseId: caseId,
    id: documentId,
    source,
    user,
    downloadFileName,
  },
});

export const fetchCaseDiagnoses = (
  id: number,
  status?: string | Case.status,
  options = { ai_only: false }
) => ({
  type: CASE_DIAGNOSES_REQUEST,
  payload: {
    id,
    status,
    ai_only: options?.ai_only,
  },
});

export const createCaseDiagnosis = (caseDiagnosis: CaseDiagnosis) => ({
  type: DIAGNOSE_REQUEST,
  payload: { caseDiagnosis },
});

export const updateCaseDiagnosis = (
  id: number,
  caseDiagnosis: Partial<CaseDiagnosis>
) => ({
  type: UPDATE_DIAGNOSIS_REQUEST,
  payload: { id, caseDiagnosis },
});

export const initiateModifyReportProcedure = (
  caseId: number,
  source:
    | CaseDiagnosis.source.ADDEND
    | CaseDiagnosis.source.AMEND
    | CaseDiagnosis.source.CORRECTION
) => ({
  type: MODIFY_REPORT_REQUEST,
  payload: {
    case_id: caseId,
    source,
  },
});

export const cancelModifyReportProcedure = (caseId: number) => ({
  type: CANCEL_MODIFY_REPORT_REQUEST,
  payload: { case_id: caseId },
});

export const editScreenDiagnoses = (case_id: number) => ({
  type: EDIT_SCREEN_DIAGNOSES_REQUEST,
  payload: { case_id: case_id },
});

export const cancelEditScreenDiagnoses = (case_id: number) => ({
  type: CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST,
  payload: { case_id: case_id },
});

export const requestSignout = (
  id: number,
  navigate: NavigateFunction,
  signature_id?: number,
  report_type?: CaseReport.report_type,
  location_code_id?: number
) => ({
  type: CASE_SIGN_OUT_REQUEST,
  payload: {
    id,
    navigate,
    signature_id,
    report_type,
    location_code_id: location_code_id || undefined, // localStorage will store `0`
  },
});

export const undoSignout = (id: number) => ({
  type: CASE_UNDO_SIGN_OUT_REQUEST,
  payload: id,
});

type StainOrderArgs = Parameters<typeof ApiService.createLabOrder>[0];

export const stainOrderRequest: AsyncSagaAction<StainOrderArgs> =
  createAsyncSaga(
    STAIN_ORDER_REQUEST,
    STAIN_ORDER_SUCCESS,
    STAIN_ORDER_FAILURE,
    ({ requestBody }) => `case/${requestBody.case_id}/create-stain-order`,
    ({ requestBody }: StainOrderArgs) =>
      ApiService.createLabOrder({ requestBody })
  );

type FetchSigneeArgs = Parameters<typeof ApiService.retrieveUser>[0];

export const fetchSignee: AsyncSagaAction<FetchSigneeArgs> = createAsyncSaga(
  REPORT_SIGNEE_REQUEST,
  REPORT_SIGNEE_SUCCESS,
  REPORT_SIGNEE_FAILURE,
  ({ id }: FetchSigneeArgs) => `signee-${id}`,
  ({ id }: FetchSigneeArgs) => ApiService.retrieveUser({ id })
);

type FetchPatientMatchesArgs = {
  case_id: number;
};
export const fetchPatientMatches: AsyncSagaAction<FetchPatientMatchesArgs> =
  createAsyncSaga(
    CASE_PATIENT_MATCHES_REQUEST,
    CASE_PATIENT_MATCHES_SUCCESS,
    CASE_PATIENT_MATCHES_FAILURE,
    ({ case_id }: FetchPatientMatchesArgs) => `patient-history-${case_id}`,
    ({ case_id }: FetchPatientMatchesArgs) =>
      ApiService.matchesCase({ id: case_id })
  ) as AsyncSagaActionNoArgs;

type updateSpecimenDetailsArgs = Parameters<
  typeof ApiService.partialUpdateSpecimen
>[0];

export const updateSpecimenDetails = createAsyncSaga(
  CASE_UPDATE_SPECIMEN_REQUEST,
  CASE_UPDATE_SPECIMEN_SUCCESS,
  CASE_UPDATE_SPECIMEN_FAILURE,
  ({ id }: updateSpecimenDetailsArgs) => `update-specimen-details-${id}`,
  ApiService.partialUpdateSpecimen
) as AsyncSagaAction<updateSpecimenDetailsArgs>;
